// Turn of eslint fast refresh warning:
/* eslint react-refresh/only-export-components: 0 */

import { createContext } from 'react';
import { name, version } from '../package.json';
export type * from './types';
import type {
  TrackEcommerceEvent,
  TrackEcommerceOptions,
  TrackEvent,
  TrackOptions
} from './types';

const NOT_SET = 'not-set'; // Variable for better minification
const APP_MODULE = 'application_module'; // Simplifies minification as this is used several times
const EVENT_MODULE = 'event_module'; // Simplifies minification as this is used several times

export const DEFAULT_CONTEXT_TRACKING = {
  [APP_MODULE]: NOT_SET,
  application_name: NOT_SET,
  business_area: NOT_SET,
  customer_segment: NOT_SET,
  event_source: name,
  event_version: version
};

// Helper function for better minification
const setOptions = ({ options: prev }: Tracking, next: TrackOptions = {}) => {
  // Special handling since changing application_module should update event_module IF the consumer has not set it to other value
  const isSyncedModule = prev[APP_MODULE] === prev[EVENT_MODULE];
  if (isSyncedModule && next[APP_MODULE]) next[EVENT_MODULE] = next[APP_MODULE];
  return Object.assign(prev, getUtagData({ ...prev, ...next }));
};

// Class API
export class Tracking {
  options: TrackOptions = { ...DEFAULT_CONTEXT_TRACKING };

  constructor(opt: TrackOptions) {
    setOptions(this, opt);
  }
  setOptions(opt: TrackOptions = {}) {
    setOptions(this, opt);
  }
  trackEvent(data: TrackEvent, opt?: TrackOptions) {
    trackEvent(data, setOptions(this, opt));
  }
  trackEcommerceEvent(data: TrackEcommerceEvent, opt?: TrackEcommerceOptions) {
    trackEcommerceEvent(data, setOptions(this, opt));
  }
  trackView(opt?: TrackOptions) {
    trackView(setOptions(this, opt));
  }
  trackEcommerceView(opt?: TrackEcommerceOptions) {
    trackEcommerceView(setOptions(this, opt));
  }
}

// Function API
export const trackEvent = (
  data: TrackEvent,
  opt?: TrackOptions,
  done?: () => void
) => track({ ...opt, ...data }, 'link', done);

export const trackEcommerceEvent = (
  data: TrackEcommerceEvent,
  opt?: TrackEcommerceOptions,
  done?: () => void
) => track({ ...opt, ...data }, 'link', done);

export const trackView = (opt?: TrackOptions) => track(opt, 'view');
export const trackEcommerceView = (opt?: TrackEcommerceOptions) =>
  track(opt, 'view');

export const TrackingContext = createContext<{ options: TrackOptions }>({
  options: { ...DEFAULT_CONTEXT_TRACKING }
});

type Props = { options: TrackOptions; children?: React.ReactNode };
export const TrackingProvider = ({ options, ...props }: Props) => (
  <TrackingContext.Provider value={{ options }} {...props} />
);

// Utils
let retries = 0;
function track(
  data: Record<string, unknown> = {},
  method: 'link' | 'view',
  done?: () => void
) {
  // Bind window.utag so it has access to the correct "this" scope
  // ref: https://gjensidige.slack.com/archives/C04HGAW004S/p1700816246504359
  const send = window.utag?.[method].bind(window.utag);
  if (send) done ? send(getUtagData(data), done) : send(getUtagData(data));
  else if (retries < 10) setTimeout(() => track(data, method), 100, retries++);
  else console.error(`window.utag.${method} not found `, data);
}

function getUtagData(raw: Record<string, unknown>) {
  const data: typeof raw = { ...DEFAULT_CONTEXT_TRACKING };

  Object.entries(raw).forEach(([key, val]) => {
    const isFormat = key === 'eventLabel' || key === 'eventStatus';
    const snakeKey = key.replace(/[A-Z]/g, '_$&').toLowerCase();

    if (isFormat && val) val = `${val}`.toLowerCase().split(' ').join('-');
    data[snakeKey] = val;
  });

  // Some duplicates, to comply with https://confluence.gjensidige.dev/confluence/x/KwZyBQ
  return {
    ...data,
    [EVENT_MODULE]: data[EVENT_MODULE] || data[APP_MODULE],
    page_pathname: data.page_pathname || getPagePath(data),
    measure_source: data.event_source,
    measure_version: data.event_version
  };
}

function getPagePath(opt: TrackOptions) {
  const selfPath = window.location.pathname.replace(/\/$/, ''); // Remove tailing slash
  const pagePath = `${selfPath}/${opt.application_name}/${opt[APP_MODULE]}/${opt.scenario_name}/${opt.scenario_step}`;
  return pagePath
    .replace(/\/(undefined|null|not-set)/g, '')
    .replace('/Din+side/', '/Din-side/');
}
